import { useAppPreferences } from "../../../lib/preferences";
import { Facts, LoadState, RecordLink, Section, StatusChip, useFormatters } from "../ui";
import { campaignStatusKey, deriveCampaignState } from "./campaignState";
import {
  budgetRemainingDisplay,
  configuredCountDisplay,
  displayCount,
  displayMoney,
  usesRemainingDisplay,
} from "./money";
import {
  redemptionClosedAt,
  redemptionFinalCredit,
  redemptionGrossCommission,
  redemptionReservedAt,
} from "./promotionAdapters";
import type { PromotionRecord, PromotionRedemptionRow } from "./types";
import "./promotions.css";

function moneyOrUnknown(value: string | null | undefined, t: (key: string) => string): string {
  return value === null || value === undefined || value.trim() === "" ? t("Unknown") : displayMoney(value);
}

function scopeText(values: string[], emptyLabel: string): string {
  return values.length === 0 ? emptyLabel : values.join(", ");
}

/**
 * Read-only campaign detail. Shows the current revision configuration,
 * truthful metrics (reserved vs consumed vs refunded, never merged), and
 * an ID-only redemption drilldown. All actions are callbacks; the
 * container owns confirmation, reasons, and server calls.
 */
export function PromotionDetail({
  canEdit,
  getBookingHref,
  onPause,
  onResume,
  onRevise,
  record,
  redemptions = [],
  redemptionsError = null,
  redemptionsLoading = false,
  onRetryRedemptions,
  venueNames = {},
}: {
  canEdit: boolean;
  getBookingHref?: (bookingId: string) => string;
  onPause?: (record: PromotionRecord) => void;
  onResume?: (record: PromotionRecord) => void;
  onRevise?: (record: PromotionRecord) => void;
  record: PromotionRecord;
  redemptions?: PromotionRedemptionRow[];
  redemptionsError?: string | null;
  redemptionsLoading?: boolean;
  onRetryRedemptions?: () => void;
  /** Venue id -> display name, supplied by the container. Unknown ids show as-is, never invented. */
  venueNames?: Record<string, string>;
}) {
  const { t } = useAppPreferences();
  const { dateTime } = useFormatters();
  const config = record.current_revision.configuration;
  const metrics = record.metrics;
  const state = deriveCampaignState(record);

  const savings =
    config.discount_type === "percent"
      ? `${config.discount_percent ?? t("Unknown")}%`
      : displayMoney(config.fixed_amount);
  const budgetLeft = budgetRemainingDisplay(config.budget_amount, metrics.remaining_budget_amount);
  const usesLeft = usesRemainingDisplay(config.max_uses, metrics.remaining_uses);
  const perCustomer = configuredCountDisplay(config.per_customer_max_uses);
  const canRevise = canEdit && onRevise !== undefined;
  const canPause = canEdit && record.is_active && onPause !== undefined;
  const canResume = canEdit && !record.is_active && onResume !== undefined;

  return (
    <div className="promo-detail">
      <Section
        actions={
          canRevise || canPause || canResume ? (
            <div className="console-button-row">
              {canRevise ? (
                <button className="ops-button" data-size="sm" data-variant="primary" onClick={() => onRevise?.(record)} type="button">
                  {t("Revise terms")}
                </button>
              ) : null}
              {canPause ? (
                <button className="ops-button" data-size="sm" data-variant="secondary" onClick={() => onPause?.(record)} type="button">
                  {t("Pause")}
                </button>
              ) : null}
              {canResume ? (
                <button className="ops-button" data-size="sm" data-variant="secondary" onClick={() => onResume?.(record)} type="button">
                  {t("Resume")}
                </button>
              ) : null}
            </div>
          ) : null
        }
        description={
          canEdit
            ? t("New terms apply to future bookings. Accepted holds keep the terms they were given.")
            : t("Support accounts can read campaigns. Only admins can create or change them.")
        }
        title={config.name_en}
      >
        <Facts
          items={[
            [t("Arabic name"), config.name_ar],
            [t("State"), <StatusChip key="state" status={campaignStatusKey(state)} />],
            [t("How players get it"), config.offer_mode === "code" ? t("Code: {code}", { code: record.code ?? "" }) : t("Automatic")],
            [t("Savings"), savings],
            [t("Maximum saving per booking"), config.max_discount_amount ? displayMoney(config.max_discount_amount) : t("No cap")],
            [t("Revision"), String(record.current_revision.revision_number)],
          ]}
        />
      </Section>

      <Section description={t("The window uses the booked start in the venue's timezone.")} title={t("Schedule and scope")}>
        <Facts
          items={[
            [t("Starts at"), dateTime(config.starts_at)],
            [t("Ends at"), config.ends_at ? dateTime(config.ends_at) : t("No end")],
            [t("Days"), scopeText(config.weekdays.map((day) => t(["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"][day] ?? String(day))), t("Every day"))],
            [
              t("Daily window"),
              config.local_start_time || config.local_end_time
                ? `${config.local_start_time ?? "—"} – ${config.local_end_time ?? "—"}`
                : t("All day"),
            ],
            [t("Venues"), scopeText(config.venue_ids.map((id) => venueNames[id] ?? id), t("Everywhere"))],
            [t("Unit types"), scopeText(config.unit_types.map((type) => t(type)), t("Everything"))],
            [t("First booking only"), config.first_booking_only ? t("Yes") : t("No")],
          ]}
        />
      </Section>

      <Section description={t("Limits count across all revisions. Lowering them never changes already accepted bookings.")} title={t("Budget and uses")}>
        <div className="console-card-grid">
          <div className="ops-stat-card">
            <p className="ops-stat-label">{t("Budget left")}</p>
            <p className="ops-stat-value">
              {budgetLeft.kind === "unlimited"
                ? t("No limit")
                : budgetLeft.kind === "unknown"
                  ? t("Unknown")
                  : displayMoney(budgetLeft.value)}
            </p>
          </div>
          <div className="ops-stat-card">
            <p className="ops-stat-label">{t("Uses left")}</p>
            <p className="ops-stat-value">
              {usesLeft.kind === "unlimited"
                ? t("No limit")
                : usesLeft.kind === "unknown"
                  ? t("Unknown")
                  : displayCount(usesLeft.value)}
            </p>
          </div>
          <div className="ops-stat-card">
            <p className="ops-stat-label">{t("Uses per customer")}</p>
            <p className="ops-stat-value">
              {perCustomer.kind === "unlimited" ? t("No limit") : displayCount(perCustomer.value)}
            </p>
          </div>
        </div>
        <Facts
          items={[
            [t("Reserved (held) uses"), displayCount(metrics.reserved_uses)],
            [t("Consumed (completed, paid) uses"), displayCount(metrics.consumed_uses)],
            [t("Released uses"), displayCount(metrics.released_uses)],
            [t("Reserved credit"), moneyOrUnknown(metrics.reserved_credit_amount, t)],
            [t("Consumed credit"), moneyOrUnknown(metrics.consumed_credit_amount, t)],
            [t("Released credit"), moneyOrUnknown(metrics.released_credit_amount, t)],
            [t("Refunded credit"), moneyOrUnknown(metrics.refunded_credit_amount, t)],
          ]}
        />
        <p className="console-muted">
          {t("Reserved credit is quoted and held. Consumed credit is actual completed and paid savings. Refunded credit reversed part of it later.")}
        </p>
      </Section>

      <Section description={t("Booking IDs only. Customer contact details are never shown here.")} title={t("Redemptions")}>
        <LoadState
          empty={t("No redemptions recorded for this campaign.")}
          error={redemptionsError}
          isEmpty={redemptions.length === 0}
          loading={redemptionsLoading}
          onRetry={onRetryRedemptions ?? (() => undefined)}
        >
          <div className="console-table-wrap">
            <table className="console-table">
              <thead>
                <tr>
                  <th scope="col">{t("Booking")}</th>
                  <th scope="col">{t("Status")}</th>
                  <th scope="col">{t("Reserved credit")}</th>
                  <th scope="col">{t("Final credit")}</th>
                  <th scope="col">{t("Gross commission (before booking savings)")}</th>
                  <th scope="col">{t("Reserved at")}</th>
                </tr>
              </thead>
              <tbody>
                {redemptions.map((row) => {
                  const finalCredit = redemptionFinalCredit(row);
                  const reservedAt = redemptionReservedAt(row);
                  const closedAt = redemptionClosedAt(row);
                  const gross = redemptionGrossCommission(row);
                  return (
                    <tr key={row.booking_id}>
                      <td>
                        {getBookingHref ? (
                          <RecordLink to={getBookingHref(row.booking_id)}>{row.booking_id}</RecordLink>
                        ) : (
                          row.booking_id
                        )}
                        {row.venue_id ? (
                          <span className="console-muted block">{venueNames[row.venue_id] ?? row.venue_id}</span>
                        ) : null}
                      </td>
                      <td>
                        <StatusChip status={row.status} />
                      </td>
                      <td>{moneyOrUnknown(row.reserved_credit_amount, t)}</td>
                      <td>{moneyOrUnknown(finalCredit, t)}</td>
                      <td>
                        {gross.value === null ? (
                          t("Unknown")
                        ) : (
                          <>
                            {displayMoney(gross.value)}
                            <span className="console-muted block">
                              {gross.basis === "final" ? t("Final") : t("Reserved estimate")}
                            </span>
                          </>
                        )}
                      </td>
                      <td>
                        {reservedAt ? dateTime(reservedAt) : "—"}
                        {closedAt ? (
                          <span className="console-muted block">{dateTime(closedAt)}</span>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="console-muted">
            {t("Before booking savings; reserved or finalized amount, not current earnings.")}
          </p>
        </LoadState>
      </Section>
    </div>
  );
}
